import React, { useEffect, useState } from "react";
import { initBx24, getBxAuth } from "./services/bx24";
import { apiLogin, setJwt } from "./services/api";
import { BxUser, AccessInfo } from "./types";
import { Loader } from "./components/Loader";
import { ConnectorsList } from "./components/ConnectorsList";
import { Dashboard } from "./components/Dashboard";
import { AccessSettings } from "./components/AccessSettings";
import { LayoutGrid, Settings, LogOut, Terminal, ArrowRight, Share2, ShieldCheck, User } from "lucide-react";

function App() {
  const [initializing, setInitializing] = useState(true);
  const [user, setUser] = useState<BxUser | null>(null);
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const [activeTab, setActiveTab] = useState<"dashboard" | "settings">("dashboard");
  const [settingsView, setSettingsView] = useState<"connectors" | "access">("connectors");
  const [showManualLogin, setShowManualLogin] = useState(false);
  const [manualJson, setManualJson] = useState("");

  useEffect(() => {
    const authFlow = async () => {
      try {
        await initBx24();
        const authData = getBxAuth();
        
        if (authData) {
            const res = await apiLogin({
              accessToken: authData.access_token,
              refreshToken: authData.refresh_token,
              domain: authData.domain,
            });
            completeLogin(res);
        } else {
            const stored = localStorage.getItem("bx_manual_auth");
            if (stored) {
                setManualJson(stored);
                try {
                    const parsed = JSON.parse(stored);
                    const res = await apiLogin(parsed);
                    completeLogin(res);
                } catch (e) {
                    setShowManualLogin(true);
                    setInitializing(false);
                }
            } else {
                setShowManualLogin(true);
                setInitializing(false);
            }
        }
      } catch (err: any) {
        setError(err.message || "Ошибка авторизации.");
        setInitializing(false);
      }
    };

    authFlow();
  }, []);

  const completeLogin = (data: any) => {
      setJwt(data.jwt);
      setUser(data.bxUser);
      setAccess(data.access);
      setInitializing(false);
      setShowManualLogin(false);
  };

  const handleManualLogin = async () => {
    setError(null);
    setInitializing(true);
    try {
        const parsed = JSON.parse(manualJson);
        localStorage.setItem("bx_manual_auth", manualJson);
        const res = await apiLogin(parsed);
        completeLogin(res);
    } catch (e: any) {
        setError(e.message);
        setInitializing(false);
    }
  };

  if (initializing) return <Loader message="Загрузка данных..." />;

  if (showManualLogin) {
      return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
            <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl w-full max-w-lg border border-slate-200 animate-in zoom-in-95 duration-300">
                <div className="flex items-center space-x-4 mb-8">
                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200">
                        <Terminal className="text-white w-6 h-6" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-slate-800 tracking-tight">Разработка</h2>
                        <p className="text-xs text-slate-400 font-medium">Введите JSON авторизации</p>
                    </div>
                </div>

                {error && <div className="mb-6 p-4 bg-rose-50 text-rose-600 text-xs font-bold rounded-2xl border border-rose-100">{error}</div>}

                <textarea 
                    value={manualJson}
                    onChange={(e) => setManualJson(e.target.value)}
                    className="w-full h-48 font-mono text-xs p-5 border border-slate-100 rounded-3xl outline-none bg-slate-50 text-slate-700 resize-none transition-all focus:ring-4 focus:ring-blue-100 focus:bg-white"
                    placeholder={`{ "accessToken": "...", "domain": "..." }`}
                />
                
                <button onClick={handleManualLogin} className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-3xl transition-all flex items-center justify-center shadow-xl shadow-blue-200 active:scale-95 uppercase tracking-widest text-xs">
                    Продолжить <ArrowRight className="w-4 h-4 ml-2" />
                </button>
            </div>
        </div>
      );
  }

  return (
    <div className="min-h-screen flex flex-col font-sans bg-slate-50 selection:bg-blue-600 selection:text-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200">
              <LayoutGrid className="text-white w-5 h-5" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tighter">oMedia</h1>
          </div>
          
          <div className="flex items-center space-x-4">
             {user && (
                <div className="flex items-center space-x-4 bg-slate-100/50 p-1.5 pr-4 rounded-full border border-slate-200">
                    <img src={user.PERSONAL_PHOTO || "https://ui-avatars.com/api/?name=" + user.NAME} className="w-9 h-9 rounded-full border-2 border-white shadow-sm" alt="" />
                    <div className="hidden sm:block text-left">
                        <p className="font-black text-slate-800 text-xs leading-none mb-1">{user.NAME} {user.LAST_NAME}</p>
                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">
                            {access?.isBitrixAdmin ? "Админ" : access?.hasModeratorAccess ? "Модератор" : "Автор"}
                        </p>
                    </div>
                </div>
             )}
          </div>
        </div>
      </header>

      {/* Nav */}
      <nav className="bg-white border-b border-slate-200 sticky top-20 z-40">
         <div className="max-w-7xl mx-auto px-6 flex">
            <button onClick={() => setActiveTab("dashboard")} className={`py-5 px-8 font-black text-xs uppercase tracking-widest transition-all border-b-2 ${activeTab === "dashboard" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                Статистика
            </button>
            {(access?.isBitrixAdmin || access?.canManageCreators || access?.canManageModerators) && (
              <button onClick={() => setActiveTab("settings")} className={`py-5 px-8 font-black text-xs uppercase tracking-widest transition-all border-b-2 ${activeTab === "settings" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                  Настройки
              </button>
            )}
         </div>
      </nav>

      {/* Main */}
      <main className="flex-grow max-w-7xl mx-auto w-full p-6 lg:p-10">
            {activeTab === "dashboard" && <Dashboard access={access} currentUser={user} />}
            {activeTab === "settings" && (
                <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex justify-center">
                        <div className="bg-white p-1.5 rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 inline-flex items-center">
                            <button onClick={() => setSettingsView('connectors')} className={`px-8 py-3 rounded-[1.5rem] text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${settingsView === 'connectors' ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'text-slate-400 hover:text-slate-600'}`}>
                                <Share2 className="w-4 h-4" /> Каналы
                            </button>
                            {(access?.isBitrixAdmin || access?.canManageCreators || access?.canManageModerators) && (
                              <button onClick={() => setSettingsView('access')} className={`px-8 py-3 rounded-[1.5rem] text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${settingsView === 'access' ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'text-slate-400 hover:text-slate-600'}`}>
                                  <ShieldCheck className="w-4 h-4" /> Доступ
                              </button>
                            )}
                        </div>
                    </div>

                    <div className="animate-in fade-in duration-300">
                        {settingsView === 'connectors' ? <ConnectorsList onAccountsUpdated={() => {}} /> : <AccessSettings access={access} />}
                    </div>
                </div>
            )}
      </main>
    </div>
  );
}

export default App;